import { considerTimePeriodCategory } from './menu';
import { Category } from '../generated-interfaces/graphql';
import {
  IBrandMenu,
  ICategoryWithMealPeriod,
  IMUItem,
  IBrandMenuOrderConstraint,
  IBrandMenuOrderItem,
  IRestaurantMealPeriod,
  IRestaurantUnavailableEntity,
  IUnavailableEntity,
  IConstraint,
  IUtterance,
} from '../types/brandMenu';
import { CartItem, CartModifierGroup } from './cart';
import {
  extractItemConversionBusinessRules,
  getRestaurantGroups,
  getTopLevelItems,
  mergeUnavailableEntitiesByName,
  parseCategoryAndMealPeriods,
  convertToMenuModuleOrder,
  getGreetingDetails,
  SYSTEM_QUESTION_CONSTRAINT,
  isPromptCarrierName,
} from './menuUnification.utils';
import { UPSELL, AUTO_COMBOS } from '../constants';
import {
  UNIFIED_MENU_CODE_NAME_MAPPING,
  UNIFIED_MENU_MOD_SYMBOL_MAPPING,
  GREETING_PREFIX,
} from './menuUnification.constants';
import logger from './logger';
import { convertToMapUsingName } from '.';
import { buildIntentCardsFromBM } from './menuUnification.utils';

export const convertToBrandMenuCategories = (
  categories: Category[],
  brandMenu: IBrandMenu
) => {
  const brandMenuItems: string[] = [];
  brandMenu.items.forEach((item: IMUItem) => brandMenuItems.push(item.name));
  return [
    { ...categories[0], name: 'Support Brand Menu', menuItems: brandMenuItems },
  ];
};

export const getMenuModuleConstraints = (
  children: CartModifierGroup,
  cartItemsQuantity: Record<string, number>,
  forReadback: boolean
) => {
  const selectedItems = Object.values(children.selectedItems).reduce(
    (acc, item) => {
      acc.push(
        convertToMenuModuleOrder(item, cartItemsQuantity, true, forReadback)
      );
      return acc;
    },
    [] as IBrandMenuOrderItem[]
  );
  return {
    constraint_name: children.name,
    items: selectedItems as IBrandMenuOrderItem[],
  } as IBrandMenuOrderConstraint;
};

export const fillConstraints = (
  cartItem: CartItem,
  cartItemsQuantity: Record<string, number>,
  forReadback: boolean = false
) => {
  const { childModifierGroups } = cartItem;

  // HITL-1675: sentinel groups are excluded from the conversion entirely; the
  // item itself and its real constraints ship unchanged. Matched on the raw
  // group id as well as the name, since a brand menu may give the group a
  // display name that differs from its id.
  const orderGroups = Object.values(childModifierGroups ?? {}).filter(
    (child) =>
      child?.name !== SYSTEM_QUESTION_CONSTRAINT &&
      child?.menuModifierGroupId !== SYSTEM_QUESTION_CONSTRAINT
  );

  if (orderGroups.length) {
    return orderGroups.reduce((acc, child) => {
      acc.push(getMenuModuleConstraints(child, cartItemsQuantity, forReadback));
      return acc;
    }, [] as IBrandMenuOrderConstraint[]);
  }

  return undefined;
};

/**
 * Is this selection the carrier group's decline option? Checked against the
 * group the menu declares (`upsell_decline_items` / `upsell_declined_item`),
 * plus the literal names the manual decline guard matches
 * (CartItemDetail.onMenuItemModifierSelect).
 *
 * Read off the FIRST child modifier group — the very object whose selection
 * the promotion below lifts (`constraints[0]` is built from it, and a
 * `CartModifierGroup` is a copy of the parsed group, so it carries the decline
 * fields) — rather than looked up by name in a second map that could drift.
 */
const isCarrierDeclineSelection = (
  cartItem: CartItem,
  selectionName: string | undefined
): boolean => {
  const name = (selectionName ?? '').toLowerCase();
  if (!name) return false;
  if (name === 'decline' || name === 'declined') return true;

  const upsellGroup = Object.values(cartItem.childModifierGroups ?? {})[0];
  if (!upsellGroup) return false;
  return (
    (upsellGroup.upsell_decline_items ?? []).some(
      (declineName) => declineName?.toLowerCase() === name
    ) || (upsellGroup.upsell_declined_item ?? '').toLowerCase() === name
  );
};

export const buildUnifiedMenuModuleOrder = (
  cartItem: CartItem,
  cartItemsQuantity: Record<string, number>,
  isModifier: boolean = false,
  forReadback: boolean = false
) => {
  // HITL-1675: a TTS-prompt carrier (e.g. `/pepsi:` from the AI's
  // not_on_menu_* hydration, imported at takeover after "Cannot find
  // fullMainItem" failed to resolve it) is spoken content, not order content —
  // sending it verbatim is a guaranteed 400 invalid_items ("Item '/pepsi' not
  // found in brand menu"). /upsell carriers pass: the promotion below is
  // their handling, together with the HITL-1652 decline rules.
  if (isPromptCarrierName(cartItem.name)) {
    return undefined;
  }

  const menuModuleItems = convertToMenuModuleOrder(
    cartItem,
    cartItemsQuantity,
    isModifier,
    forReadback
  );
  if (cartItem.name.includes(UPSELL)) {
    const upsellConstraint = menuModuleItems?.constraints?.[0];
    const upsellItem = upsellConstraint?.items?.[0] as IBrandMenuOrderItem;
    if (!upsellItem) {
      return undefined;
    }
    // HITL-1652: a declined carrier is dropped exactly like an unanswered one.
    // Promoting the decline shipped a literal top-level `decline` item, which
    // the menu module 400s (invalid_items) — failing the whole order. The
    // manual decline click deletes the carrier before it gets here; this is
    // the safety net for every path that writes the selection directly
    // (the final-order autofill's selectModifiers, hypothesis imports).
    if (isCarrierDeclineSelection(cartItem, upsellItem.item)) {
      return undefined;
    }
    return { ...upsellItem, quantity: menuModuleItems.quantity };
  }
  return menuModuleItems;
};

export const processBrandMenu = (
  brandMenu: IBrandMenu,
  restaurantCode: string,
  restaurantUnavailableEntities: IRestaurantUnavailableEntity[],
  formattedRestaurantMealPeriods: IRestaurantMealPeriod[],
  timezone: string = '',
  utterances?: IUtterance[]
) => {
  // Get unavailable items from both brand menu unavailable entities  and restaurant unavailable entities
  const brandUnavailableEntities = brandMenu.unavailable_entities;
  // restaurantCode parameter is already the correct restaurant code from the API response
  // (which may differ from the selected restaurant code when mirroring)
  const restaurantGroups = getRestaurantGroups(
    restaurantCode,
    brandMenu.restaurant_groups
  );

  const itemConversionBusinessRules = extractItemConversionBusinessRules(
    brandMenu.business_rules,
    brandMenu.restaurant_groups,
    restaurantCode
  );

  /*
    Build unavailableItemsByName object by:
       - Using brand unavailable entities
       - Using restaurant unavailable entities
  */
  const unavailableItemsByName = {};
  mergeUnavailableEntitiesByName(
    brandUnavailableEntities.filter((unavailableEntity) =>
      restaurantGroups.includes(unavailableEntity.restaurant_group)
    ),
    unavailableItemsByName,
    timezone
  );
  mergeUnavailableEntitiesByName(
    restaurantUnavailableEntities as IUnavailableEntity[],
    unavailableItemsByName,
    timezone
  );

  const topLevelConstraints: IConstraint[] = [];
  const bmWelcomeConstraints: IConstraint[] = [];
  const bmWelcomeItems: Record<string, IMUItem> = {};

  for (const constraint of brandMenu.constraints) {
    const { is_top_level, name } = constraint;
    if (is_top_level) {
      topLevelConstraints.push(constraint);
    }
    if (name?.toLowerCase().startsWith(GREETING_PREFIX)) {
      bmWelcomeConstraints.push(constraint);
    }
  }

  const itemsByName: Record<string, IMUItem> = {};
  const bmItemsSynonyms: Record<string, Array<string>> = {};

  brandMenu.items?.forEach((entry) => {
    const { name, customer_synonyms = [] } = entry;

    itemsByName[name] = entry;
    if (customer_synonyms.length) {
      bmItemsSynonyms[name] = [...customer_synonyms];
    }

    if (name.toLowerCase().startsWith(GREETING_PREFIX)) {
      bmWelcomeItems[name] = entry;
    }
  });

  // Process the meal period in items to get category with time period
  const restaurantMealPeriodsByName = convertToMapUsingName(
    formattedRestaurantMealPeriods
  );

  const categoriesWithMealPeriod: ICategoryWithMealPeriod[] =
    topLevelConstraints.length
      ? topLevelConstraints.reduce((acc, constraint) => {
          const { name, child_items, meal_period } = constraint;
          if (!child_items) return acc;

          const itemMealPeriods = Array.from(
            new Set<string>(
              child_items
                .map((itemName) => itemsByName[itemName]?.meal_period)
                .filter((mealPeriod): mealPeriod is string => !!mealPeriod)
            )
          );

          acc.push({
            name,
            item_names: child_items,
            mealPeriods: meal_period
              ? [...itemMealPeriods, meal_period]
              : [...itemMealPeriods],
          });

          logger.debug({
            constraintName: name,
            itemMealPeriods,
            categoryMealPeriod: meal_period,
            message: 'Handle top level constraint meal periods',
          });
          return acc;
        }, [] as ICategoryWithMealPeriod[])
      : brandMenu.categories.reduce((acc, category) => {
          if (!category?.item_names) return acc;

          const itemMealPeriods = Array.from(
            new Set<string>(
              category.item_names
                .map((itemName) => itemsByName[itemName]?.meal_period)
                .filter((mealPeriod): mealPeriod is string => !!mealPeriod)
            )
          );

          acc.push({
            ...category,
            mealPeriods: [...itemMealPeriods],
          });
          return acc;
        }, [] as ICategoryWithMealPeriod[]); // Adjust the type if necessary; TODO: remove category once the backend finishes transitioning

  const { categoriesWithTimePeriod, alwaysAvailableCategoryWithTimePeriods } =
    parseCategoryAndMealPeriods(
      categoriesWithMealPeriod,
      restaurantMealPeriodsByName
    );

  const categoriesWithActiveTimePeriod = considerTimePeriodCategory(
    categoriesWithTimePeriod,
    timezone
  );
  const availableCategoryWithTimePeriod = categoriesWithActiveTimePeriod.filter(
    (category) => !category.inactive
  );

  const alwaysAvailableCategories = considerTimePeriodCategory(
    alwaysAvailableCategoryWithTimePeriods,
    timezone
  );

  // Populate the top level menu items using the active categories and unavailable items
  const topLevelMenuItems = getTopLevelItems(
    [
      ...categoriesWithActiveTimePeriod.filter(
        (category) => category.name !== AUTO_COMBOS
      ),
      ...alwaysAvailableCategories,
    ],
    itemsByName,
    unavailableItemsByName,
    convertToMapUsingName(brandMenu.constraints),
    timezone,
    restaurantMealPeriodsByName,
    restaurantGroups
  );

  const greetingDetails = getGreetingDetails(
    bmWelcomeItems,
    restaurantMealPeriodsByName,
    timezone,
    bmWelcomeConstraints
  );

  // Build intent cards only from utterances (conversational_reply) loaded via utterances_url
  const intentCards = buildIntentCardsFromBM(utterances);

  return {
    topLevelMenuItems,
    brandMenu,
    restaurantCode,
    itemConversionBusinessRules,
    bmItemsSynonyms,
    restaurantUnavailableEntities,
    categoriesWithTimePeriod,
    availableCategoryWithTimePeriod,
    alwaysAvailableCategories,
    unavailableItemsByName,
    restaurantMealPeriodsByName,
    currentRestaurantGroups: restaurantGroups,
    greetingDetails,
    // Hardcode the following fields for compatibility
    processedCategoryAndTimePeriodJSON: {},
    processedMenuJSON: {},
    processedModGroupsJSON: {},
    persistentVoiceProps: {},
    codeNameMapping: UNIFIED_MENU_CODE_NAME_MAPPING,
    modSymbolMapping: UNIFIED_MENU_MOD_SYMBOL_MAPPING,
    unavailableItems: {},
    autoComboPrpIds: [],
    comboPrpIdToALaCartePrpId: {},
    intentCards,
  };
};
